import { useState, useEffect } from 'react';
import axiosInstance from '../utils/axiosInstance';
import { Controller } from 'react-hook-form';
import InputFieldAddress from '../components/InputFieldAddress';
import DefinitionRow from '../components/DefinitionRow';
import DuplicateList from './DuplicateList';
import { Stack, TextField, MenuItem, InputAdornment, Typography, Tooltip, IconButton } from '@mui/material';
import { HelpOutline } from "@mui/icons-material";
import { getFields, clientFields } from '../utils/fields';
const ClientInfoForm = ({
    clientData, setClientData, refetch, isEditMode, setIsEditMode,
    register, errors, setValue, control }) => {
    const fieldsToInclude = ['client_code', 'client_name', 'branch_name', 'postal_code', 'client_phone',
        'client_fax', 'industry_id', 'business_description', 'website_url', 'payment_method_id',
        'payment_terms', 'credit_terms'];
    let clientItems = getFields(clientFields, fieldsToInclude);
    const [isFocused, setIsFocused] = useState(false);
    const [paymentOptions, setPaymentOptions] = useState([]);
    const [industryOptions, setIndustryOptions] = useState([]);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const [indRes, payRes] = await Promise.all([
                    axiosInstance.get('/list/industries', { withCredentials: false }),
                    axiosInstance.get('/list/payment-methods', { withCredentials: false }),
                ]);
                setIndustryOptions(indRes.data || []);
                setPaymentOptions(payRes.data || []);
            } catch (err) {
                console.error(err);
            }
        };
        fetchOptions();
    }, []);
    useEffect(() => {
        if (!clientData) return;
        const keys = [
            'client_code', 'client_name', 'branch_name', 'client_phone', 'client_fax',
            'industry_id', 'business_description', 'website_url', 'payment_method_id',
            'payment_terms', 'credit_terms', 'postal_code', 'state', 'address_line'
        ];

        keys.forEach(key => {
            if (key === 'credit_terms') {
                setValue(key, clientData[key] ?? 0); // 空なら 0 に
            } else {
                setValue(key, clientData[key] ?? '');
            }
        });
    }, [clientData, setValue]);

    return (
        <Stack>
            <DefinitionRow key='client_code' label='取引先コード'>
                <Controller
                    name="client_code"
                    control={control}
                    defaultValue={clientData?.client_code || ''}
                    rules={{
                        maxLength: { value: 50, message: '50文字以内で入力してください' },
                    }}
                    render={({ field }) => (
                        <div style={{ position: 'relative' }}>
                            <TextField
                                {...field}
                                disabled={!isEditMode}
                                error={!!errors?.client_code}
                                helperText={errors?.client_code?.message}
                                inputProps={{ maxLength: 50 }}
                                onFocus={() => setIsFocused(true)}
                                onBlur={() => setIsFocused(false)}

                            />

                            <DuplicateList
                                selfId={clientData?.client_id}  // 編集中の自分を除外
                                value={field.value}            // 入力中の値をそのまま渡す
                                displayPrimary="client_code"
                                displaySecondary="client_name"
                                disabled={!isEditMode || !isFocused}
                                apiEndpoint="/client/check-client-code"
                            />
                        </div>
                    )}
                />
            </DefinitionRow>

            {clientItems.map(({ label, name, type, placeholder, validation, inputProps }) => {
                /* --- 支払条件（％表示） --- */


                /* --- 業種 --- */
                if (name === 'industry_id') {
                    return (
                        <DefinitionRow key={name} label={label}>
                            <Controller
                                name={name}
                                control={control}
                                defaultValue={clientData[name] || ''}
                                render={({ field }) => (
                                    <TextField
                                        select
                                        {...field}
                                        disabled={!isEditMode}
                                        error={!!errors?.[name]}
                                        helperText={errors?.[name]?.message}
                                        SelectProps={{ displayEmpty: true }}
                                    >
                                        <MenuItem value="">選択してください</MenuItem>
                                        {industryOptions.map((item) => (
                                            <MenuItem
                                                key={item.industry_id}
                                                value={item.industry_id}
                                            >
                                                {item.industry_name}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </DefinitionRow>
                    );
                }
                /* --- 住所 --- */
                if (name === 'postal_code') {
                    return (
                        <DefinitionRow key={name} label="営業所所在地">
                            <InputFieldAddress
                                register={register}
                                errors={errors}
                                isEditMode={isEditMode}
                                setValue={setValue}
                                control={control}
                            />
                        </DefinitionRow>
                    );
                }

                /* --- 通常入力 --- */
                return (
                    <DefinitionRow key={name} label={label}>
                        <TextField
                            type={type}
                            placeholder={placeholder}
                            disabled={!isEditMode}
                            {...register(name, validation)}
                            error={!!errors?.[name]}
                            helperText={errors?.[name]?.message}
                            inputProps={inputProps}
                            multiline
                        />
                    </DefinitionRow>
                );
            })}
            <DefinitionRow key='payment_method_id' label='支払い方法'>
                <Controller
                    name='payment_method_id'
                    control={control}
                    defaultValue={clientData['payment_method_id'] || ''}
                    render={({ field }) => (
                        <TextField
                            select
                            {...field}
                            disabled={!isEditMode}
                            error={!!errors?.['payment_method_id']}
                            helperText={errors?.['payment_method_id']?.message}
                            SelectProps={{ displayEmpty: true }}
                        >
                            <MenuItem value="">選択してください</MenuItem>
                            {paymentOptions.map((item) => (
                                <MenuItem
                                    key={item.payment_method_id}
                                    value={item.payment_method_id}
                                >
                                    {item.payment_method_name}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                />
            </DefinitionRow>
            <DefinitionRow key='credit_terms' label='仕切り率'>
                <Controller
                    name="credit_terms"
                    control={control}
                    defaultValue={clientData.credit_terms ?? ''}
                    rules={{
                        min: { value: 0, message: '0以上で入力してください' },
                        max: { value: 100, message: '100以下で入力してください' },
                    }}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            type="number"
                            disabled={!isEditMode}
                            error={!!errors?.credit_terms}
                            helperText={errors?.credit_terms?.message}
                            sx={{ maxWidth: 200 }}
                            onChange={(e) => {
                                const value = e.target.value;
                                field.onChange(value === '' ? null : parseFloat(value));
                            }}
                            value={field.value ?? ''}
                            inputProps={{ min: 0, max: 100 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">%</InputAdornment>
                                ),
                            }}
                        />
                    )}
                />
                <Tooltip
                    title={
                        <Typography variant="body2">
                            仕切り率を設定しておくと価格設定タブで一括設定できます
                        </Typography>
                    }
                    arrow
                    placement="right"
                >
                    <IconButton size="small" aria-label="help">
                        <HelpOutline fontSize="small" />
                    </IconButton>
                </Tooltip>
            </DefinitionRow>
        </Stack>
    );
};

export default ClientInfoForm;
